import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { FeedbackStatus } from '@kar-dmp/pattern-library';
import Loading from '../../../_shared/components/Loading';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import useAnalytics from '../../../_shared/hooks/useAnalytics';

import {
  StyledResearch,
  StyledResearchBox,
  StyledResearchHeading,
  StyledHeadingBox,
  StyledHeadingBoxSub,
  StyledResearchPricesBox,
  StyledResearchHeadingLink,
  NoMarketGuideDataBanner,
  NoMarketGuideDataBoldLabel
} from './Styles';
import Icon from '../../../_shared/components/Icon';

const researchTypes = [
  { name: 'Average Price', value: 'average', id: 'vdp.research.average' },
  { name: 'High', value: 'highest', id: 'vdp.research.highest' },
  { name: 'Low', value: 'lowest', id: 'vdp.research.lowest' }
];

const Research = ({
  researchUrl,
  researchPrices,
  tradeState,
  isResearchDataLoaded
}) => {
  const track = useAnalytics();

  const isResearchPricesPopulated = researchTypes
    .map(t => t.value)
    .every(t1 => researchPrices[t1] !== 0);

  const trackResearchPageView = useCallback(() => {
    track(MixpanelConfig.names.viewResearchPageFromVDP, {
      source: window.location.href.includes(ROUTES.BUYING)
        ? BidSource.MINI_VDP
        : BidSource.VDP_OVERLAY
    });
  }, [track]);

  return (
    <>
      <StyledResearch id="research" tradeState={tradeState}>
        <StyledResearchBox>
          <StyledHeadingBox id="styledHeadingBox">
            <StyledHeadingBoxSub id="styledHeadingBoxSub">
              <StyledResearchHeading data-testid="vdpResearch-title">
                {/* Research */}
                <FormattedMessage id="vdp.research.heading" />
              </StyledResearchHeading>
              {isResearchDataLoaded ? (
                <StyledResearchHeadingLink
                  href={researchUrl}
                  onClick={trackResearchPageView}
                  data-testid="vdp-open-research-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {/* Open this VIN in TradeRev research */}
                  <FormattedMessage id="vdp.research.researchVin" />
                </StyledResearchHeadingLink>
              ) : (
                <Loading width="240px" height="20px" />
              )}
            </StyledHeadingBoxSub>

            {/* Last 90 days of TradeRev market data for similar vehicles. */}
            <div data-testid="vdpResearch-subheading">
              <FormattedMessage id="vdp.research.subheading" />
            </div>
          </StyledHeadingBox>
          <StyledResearchPricesBox className="notranslate">
            {isResearchDataLoaded && isResearchPricesPopulated && (
              <ul>
                {researchTypes.map(type => (
                  <li key={type.value}>
                    {/* Average, highest, lowest bid headings */}
                    <p>
                      <FormattedMessage id={type.id} />
                    </p>
                    <p>{convertToCurrency(researchPrices[type.value])}</p>
                  </li>
                ))}
              </ul>
            )}

            {!isResearchDataLoaded && <Loading width="540px" height="140px" />}
          </StyledResearchPricesBox>
          {isResearchDataLoaded && !isResearchPricesPopulated && (
            <NoMarketGuideDataBanner
              status={FeedbackStatus.Neutral}
              data-testid="vdpResearch-noMarketguideData"
            >
              <Icon icon="TR5-Info" />
              <div>
                <FormattedMessage id="vdp.research.no.marketguide.data.part1" />
                <NoMarketGuideDataBoldLabel>
                  <FormattedMessage id="vdp.research.no.marketguide.data.part2" />
                </NoMarketGuideDataBoldLabel>
                <FormattedMessage id="vdp.research.no.marketguide.data.part3" />
              </div>
            </NoMarketGuideDataBanner>
          )}
        </StyledResearchBox>
      </StyledResearch>
    </>
  );
};

export default Research;
